import { useEffect, useRef } from 'react';

/**
 * Detects new frontend builds and self-recovers stale browser sessions without
 * surprising users who may be entering data.
 *
 * The build pipeline writes `build/version.txt` after every build. Open tabs
 * compare that file against the version they booted with. Background tabs can
 * reload automatically when they have no unsaved edits. Visible tabs stay
 * quiet unless a stale build asset actually fails to load.
 */
const POLL_INTERVAL_MS = 60 * 1000;
const HIDDEN_RELOAD_DELAY_MS = 2 * 1000;
const VERSION_PATH = '/version.txt';
const RELOAD_DEBOUNCE_MS = 60 * 1000;
const RELOAD_TIMESTAMP_KEY = 'mn_app_version_last_reload';
const UPDATE_BANNER_ID = 'mn-app-update-banner';

export const APP_UNSAVED_CHANGES_EVENT = 'mn:unsaved-changes';

const CHUNK_ERROR_PATTERNS = [
  /ChunkLoadError/i,
  /Loading chunk [\w-]+ failed/i,
  /Loading CSS chunk [\w-]+ failed/i,
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
];

async function fetchDeployedVersion(): Promise<string | null> {
  try {
    const url = `${VERSION_PATH}?t=${Date.now()}`;
    const res = await fetch(url, {
      cache: 'no-store',
      credentials: 'omit',
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
    });
    if (!res.ok) return null;
    const text = (await res.text()).trim();
    return text || null;
  } catch {
    return null;
  }
}

function safeReload(force = false): void {
  // Guard against reload loops if a stale CDN edge alternates versions.
  try {
    const last = window.sessionStorage.getItem(RELOAD_TIMESTAMP_KEY);
    const now = Date.now();
    const lastReloadAt = last ? Number(last) : 0;
    if (!force && lastReloadAt && now - lastReloadAt < RELOAD_DEBOUNCE_MS) return;
    window.sessionStorage.setItem(RELOAD_TIMESTAMP_KEY, String(now));
  } catch {
    // sessionStorage can be unavailable in private browsing. Reload anyway.
  }
  window.location.reload();
}

function unknownToMessage(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value instanceof Error) return `${value.name} ${value.message}`;
  if (value && typeof value === 'object') {
    const maybeError = value as { name?: unknown; message?: unknown };
    return `${String(maybeError.name ?? '')} ${String(maybeError.message ?? '')}`;
  }
  return '';
}

function isChunkLoadMessage(value: unknown): boolean {
  const message = unknownToMessage(value);
  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

function isStaticBuildAsset(src: string | null): boolean {
  return Boolean(src && /\/static\/(js|css)\//.test(src));
}

function getErroredAssetSrc(event: Event): string | null {
  const target = event.target;
  if (target instanceof HTMLScriptElement) return target.src;
  if (target instanceof HTMLLinkElement) return target.href;
  return null;
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.closest('[data-version-refresh-ignore]')) return false;
  if (target.isContentEditable) return true;
  if (target instanceof HTMLTextAreaElement) {
    return !target.disabled && !target.readOnly;
  }
  if (target instanceof HTMLSelectElement) {
    return !target.disabled;
  }
  if (!(target instanceof HTMLInputElement)) return false;

  const ignoredTypes = new Set([
    'button',
    'checkbox',
    'color',
    'file',
    'hidden',
    'image',
    'radio',
    'range',
    'reset',
    'search',
    'submit',
  ]);

  return !target.disabled && !target.readOnly && !ignoredTypes.has(target.type);
}

export function useAppVersion(): void {
  const initialVersionRef = useRef<string | null>(null);
  const pendingReloadRef = useRef(false);
  const hasUnsavedChangesRef = useRef(false);
  const reloadTimerRef = useRef<number | null>(null);
  const updateBannerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let canceled = false;
    let checkInFlight = false;

    const clearReloadTimer = () => {
      if (reloadTimerRef.current === null) return;
      window.clearTimeout(reloadTimerRef.current);
      reloadTimerRef.current = null;
    };

    const removeUpdateBanner = () => {
      updateBannerRef.current?.remove();
      updateBannerRef.current = null;
    };

    const showUpdateBanner = () => {
      if (updateBannerRef.current || !document.body) return;

      const banner = document.createElement('div');
      banner.id = UPDATE_BANNER_ID;
      banner.setAttribute('role', 'status');
      banner.setAttribute('aria-live', 'polite');
      banner.style.cssText = [
        'position: fixed',
        'left: 50%',
        'bottom: 18px',
        'transform: translateX(-50%)',
        'z-index: 2147483647',
        'display: flex',
        'align-items: center',
        'gap: 12px',
        'max-width: calc(100vw - 32px)',
        'padding: 12px 14px',
        'background: #111827',
        'color: #ffffff',
        'border: 1px solid rgba(255, 255, 255, 0.18)',
        'box-shadow: 0 12px 32px rgba(0, 0, 0, 0.28)',
        'font: 500 13px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      ].join(';');

      const message = document.createElement('span');
      message.textContent = 'Update available. Save your work, then refresh.';
      message.style.cssText = 'line-height: 1.35;white-space: nowrap;';

      const refreshButton = document.createElement('button');
      refreshButton.type = 'button';
      refreshButton.textContent = 'Refresh';
      refreshButton.style.cssText = [
        'appearance: none',
        'border: 1px solid rgba(255, 255, 255, 0.35)',
        'background: #ffffff',
        'color: #111827',
        'padding: 7px 10px',
        'font: inherit',
        'font-weight: 700',
        'cursor: pointer',
      ].join(';');
      refreshButton.addEventListener('click', () => safeReload(true));

      banner.append(message, refreshButton);
      document.body.appendChild(banner);
      updateBannerRef.current = banner;
    };

    const canReloadWithoutDataLoss = (allowVisibleReload: boolean): boolean => {
      if (hasUnsavedChangesRef.current) return false;
      if (document.visibilityState === 'hidden') return true;
      return allowVisibleReload;
    };

    const scheduleReload = (delayMs: number, allowVisibleReload: boolean, promptIfBlocked: boolean) => {
      if (reloadTimerRef.current !== null) return;
      reloadTimerRef.current = window.setTimeout(() => {
        reloadTimerRef.current = null;
        if (!pendingReloadRef.current) return;
        if (canReloadWithoutDataLoss(allowVisibleReload)) {
          removeUpdateBanner();
          safeReload();
          return;
        }
        if (promptIfBlocked) showUpdateBanner();
      }, delayMs);
    };

    const requestReload = (allowVisibleReload = false, promptIfBlocked = false) => {
      pendingReloadRef.current = true;

      if (canReloadWithoutDataLoss(allowVisibleReload)) {
        const delay = document.visibilityState === 'hidden' && !allowVisibleReload
          ? HIDDEN_RELOAD_DELAY_MS
          : 0;
        scheduleReload(delay, allowVisibleReload, promptIfBlocked);
        return;
      }

      clearReloadTimer();
      if (promptIfBlocked) showUpdateBanner();
    };

    const setUnsavedChanges = (dirty: boolean) => {
      if (hasUnsavedChangesRef.current === dirty) return;
      hasUnsavedChangesRef.current = dirty;
      if (!dirty && pendingReloadRef.current) requestReload();
    };

    const checkVersion = async () => {
      if (checkInFlight) return;
      checkInFlight = true;
      try {
        const remote = await fetchDeployedVersion();
        if (canceled || !remote) return;
        if (initialVersionRef.current === null) {
          initialVersionRef.current = remote;
          return;
        }
        if (remote !== initialVersionRef.current) {
          requestReload();
        }
      } finally {
        checkInFlight = false;
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        checkVersion();
        if (pendingReloadRef.current) requestReload();
        return;
      }
      checkVersion();
      if (pendingReloadRef.current) requestReload();
    };

    const onWindowError = (event: ErrorEvent | Event) => {
      const errorEvent = event as ErrorEvent;
      if (isChunkLoadMessage(errorEvent.error || errorEvent.message)) {
        requestReload(true, true);
        return;
      }
      if (isStaticBuildAsset(getErroredAssetSrc(event))) {
        requestReload(true, true);
      }
    };

    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (isChunkLoadMessage(event.reason)) {
        requestReload(true, true);
      }
    };

    const onPotentialEdit = (event: Event) => {
      if (isEditableTarget(event.target)) setUnsavedChanges(true);
    };

    const onUnsavedChangesEvent = (event: Event) => {
      const detail = (event as CustomEvent<{ dirty?: boolean; hasUnsavedChanges?: boolean }>).detail;
      const dirty = detail?.dirty ?? detail?.hasUnsavedChanges;
      if (typeof dirty === 'boolean') setUnsavedChanges(dirty);
    };

    checkVersion();
    const interval = window.setInterval(checkVersion, POLL_INTERVAL_MS);

    document.addEventListener('visibilitychange', onVisibility);
    document.addEventListener('error', onWindowError, true);
    document.addEventListener('input', onPotentialEdit, true);
    document.addEventListener('change', onPotentialEdit, true);
    document.addEventListener(APP_UNSAVED_CHANGES_EVENT, onUnsavedChangesEvent as EventListener);
    window.addEventListener('focus', checkVersion);
    window.addEventListener('pageshow', checkVersion);
    window.addEventListener('online', checkVersion);
    window.addEventListener('error', onWindowError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);
    window.addEventListener(APP_UNSAVED_CHANGES_EVENT, onUnsavedChangesEvent as EventListener);

    return () => {
      canceled = true;
      clearReloadTimer();
      removeUpdateBanner();
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
      document.removeEventListener('error', onWindowError, true);
      document.removeEventListener('input', onPotentialEdit, true);
      document.removeEventListener('change', onPotentialEdit, true);
      document.removeEventListener(APP_UNSAVED_CHANGES_EVENT, onUnsavedChangesEvent as EventListener);
      window.removeEventListener('focus', checkVersion);
      window.removeEventListener('pageshow', checkVersion);
      window.removeEventListener('online', checkVersion);
      window.removeEventListener('error', onWindowError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
      window.removeEventListener(APP_UNSAVED_CHANGES_EVENT, onUnsavedChangesEvent as EventListener);
    };
  }, []);
}
